import api from './api';
import {
  lineaTienePrecioCero,
  pedidoTieneLineasConPrecioCero,
  prepararItemsParaCreateConPrecioCero,
} from '../utils/pedidoVentaLineas';
import { toQueryDateYMD } from '../utils/formatters';
import { DocumentosKpis, normalizeDocumentosKpis } from '../utils/documentosKpis';
import { getFileNameFromContentDisposition } from '../utils/contentDisposition';
import { umDesdeDetalleTransaccion } from '../utils/umVentaDocumento';
import { normalizeUmSnapshotPedFromApi } from '../utils/umDocumentoDisplay';
import { mapLotesFromApiRaw } from '../utils/loteLineaHelpers';
import type { LoteAsignacion } from './loteService';

export interface RemitoVentaDetalleDto {
  nroIdformulario: number;
  nroItem: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  /** Cantidad en UM base. */
  cantidad?: number;
  /** Precio unitario en UM base. */
  precioUnitario?: number;
  /** UM base. */
  um?: string;
  /** Cantidad ingresada por el usuario (snapshot Ped). */
  cantidadUmped?: number;
  /** UM ingresada por el usuario. */
  umped?: string;
  /** Factor hacia UM base. */
  equivUmped?: number;
  /** Precio unitario ingresado (en umped). */
  precioUnitarioUmped?: number;
  descuento?: number;
  subtotal?: number;
  notas?: string;
  lotes?: LoteAsignacion[];
}

export interface RemitoVenta {
  nroFormulario: number;
  idCompania?: string;
  nroRemito?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  nroTipoFormulario?: string;
  tipoCliente?: string;
  nroCliente?: number;
  nombre?: string;
  direccionCliente?: string | null;
  direccionEnvio?: string | null;
  nroVendedor?: number;
  nroLista?: string;
  codigoMoneda?: number;
  importeTotal?: number;
  asentado?: string;
  descripcion?: string;
  usuario?: string;
  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
  nroUbicacionOrigen?: number;
  /** 0 o null: todavía no se informaron los bultos (se piden al imprimir). */
  bultos?: number | null;
  /** Kilogramos del remito. Al imprimir se muestra este valor y se puede modificar. */
  pesoTotal?: number | null;
  detalles?: RemitoVentaDetalleDto[];
}

export interface RemitoVentaFormularioDto {
  idCompania?: string;
  descripcion?: string;
  nroSuc?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  fechaVencim?: string | Date;
  nroCliente: number;
  nombre?: string;
  cuit?: string;
  nroLista?: string;
  nroUbicacionOrigen?: number;
  direccionEnvio?: string;
  codigoCiudadEnvio?: number;
  formaEntrega?: string;
  porcDesc?: number;
  porcDesc2?: number;
  porcDesc3?: number;
  nroFormularioFactura?: number;
}

export interface RemitoVentaCreateItemDto {
  codigoArticulo: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc?: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  notas?: string;
  um?: string;
  codigoMoneda?: number;
  cotizacion?: number;
  lotes?: import('./loteService').LoteAsignacion[];
}

export interface RemitoVentaCreateDto {
  formulario: RemitoVentaFormularioDto;
  items: RemitoVentaCreateItemDto[];
}

export interface RemitoVentaSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  fechaDesde?: Date | string;
  fechaHasta?: Date | string;
  nroCliente?: string;
  nroVendedor?: string;
  asentado?: string;
  nroSuc?: number;
}

export interface RemitoVentaPagedResult {
  items: RemitoVenta[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface AnularRemitoVentaDto {
  claveSuperusuario?: string | null;
  motivo?: string | null;
}

const itemComoDetallePrecio = (item: RemitoVentaCreateItemDto) => ({
  precioUnitario: item.precioUnitario,
  porcDesc: item.porcDesc,
  descuento: item.porcDesc,
  cantidad: item.cantidad,
  codigoArticulo: item.codigoArticulo,
  descripcionArticulo: item.descripcion,
});

const prepararItemsRemitoParaPost = (items: RemitoVentaCreateItemDto[]): RemitoVentaCreateItemDto[] => {
  if (!pedidoTieneLineasConPrecioCero(items.map(itemComoDetallePrecio))) {
    return items;
  }

  const indicesConCero = items
    .map((item, index) => (lineaTienePrecioCero(itemComoDetallePrecio(item)) ? index : -1))
    .filter((index) => index >= 0);
  const indicesSinCero = items
    .map((item, index) => (!lineaTienePrecioCero(itemComoDetallePrecio(item)) ? index : -1))
    .filter((index) => index >= 0);

  if (indicesSinCero.length > 0) {
    return indicesSinCero.map((index) => items[index]);
  }

  const preparados = prepararItemsParaCreateConPrecioCero(items.map(itemComoDetallePrecio));
  const precioSimbolico = preparados[0]?.precioUnitario === 0.01;
  if (!precioSimbolico) return items;

  return items.map((item, index) =>
    index === 0 ? { ...item, precioUnitario: 0.01 } : item,
  );
};

const extraerNroFormularioRemito = (data: unknown): number | null => {
  const raw = data as {
    nroFormulario?: unknown;
    formulario?: { nroFormulario?: unknown };
    data?: { nroFormulario?: unknown };
  };
  const candidatos = [raw?.nroFormulario, raw?.formulario?.nroFormulario, raw?.data?.nroFormulario];
  for (const value of candidatos) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const normalizeRemitoVentaFromApi = (raw: RemitoVenta): RemitoVenta => ({
  ...raw,
  detalles: (raw.detalles || (raw as { Detalles?: RemitoVentaDetalleDto[] }).Detalles || []).map((d) => {
    const row = d as RemitoVentaDetalleDto & { Lotes?: unknown; lotes?: unknown };
    const lotes = mapLotesFromApiRaw(row.lotes ?? row.Lotes);
    return {
      ...d,
      um: umDesdeDetalleTransaccion(d) || undefined,
      ...normalizeUmSnapshotPedFromApi(d),
      lotes,
    };
  }),
});

/** Cantidad/UM/precio principales = UM base (columnas normales). */
export function remitoDetalleCantidadDisplay(d: RemitoVentaDetalleDto): number {
  return Number(d.cantidad) || 0;
}

export function remitoDetalleUmDisplay(d: RemitoVentaDetalleDto): string {
  return String(d.um ?? '').trim();
}

export function remitoDetallePrecioDisplay(d: RemitoVentaDetalleDto): number {
  return Number(d.precioUnitario) || 0;
}

const remitoVentaService = {
  getRemitosVenta: async (params: RemitoVentaSearchParams): Promise<RemitoVentaPagedResult> => {
    const queryParams: Record<string, unknown> = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
      SearchTerm: params.searchTerm || undefined,
    };
    if (params.fechaDesde) {
      queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
    }
    if (params.fechaHasta) {
      queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
    }
    if (params.nroCliente) {
      queryParams.NroCliente = params.nroCliente;
    }
    if (params.nroVendedor) {
      queryParams.NroVendedor = params.nroVendedor;
    }
    if (params.asentado) {
      queryParams.Asentado = params.asentado;
    }
    if (params.nroSuc != null) {
      queryParams.NroSuc = params.nroSuc;
    }
    const response = await api.get<RemitoVentaPagedResult>('/api/RemitosVenta', {
      params: queryParams,
    });
    return {
      ...response.data,
      items: (response.data.items || []).map(normalizeRemitoVentaFromApi),
    };
  },

  getRemitosVentaKpis: async (
    params: Omit<RemitoVentaSearchParams, 'pageNumber' | 'pageSize'>,
  ): Promise<DocumentosKpis> => {
    const queryParams: Record<string, unknown> = {
      SearchTerm: params.searchTerm || undefined,
      PageNumber: 1,
      PageSize: 1,
    };
    if (params.fechaDesde) queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
    if (params.fechaHasta) queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
    if (params.nroCliente) queryParams.NroCliente = params.nroCliente;
    if (params.nroVendedor) queryParams.NroVendedor = params.nroVendedor;
    if (params.asentado) queryParams.Asentado = params.asentado;
    if (params.nroSuc != null) queryParams.NroSuc = params.nroSuc;
    const response = await api.get('/api/RemitosVenta/kpis', {
      params: queryParams,
    });
    return normalizeDocumentosKpis(response.data);
  },

  getRemitoVenta: async (id: number): Promise<RemitoVenta> => {
    const response = await api.get<RemitoVenta>(`/api/RemitosVenta/${id}`);
    return normalizeRemitoVentaFromApi(response.data);
  },

  anularRemitoVenta: async (id: number, payload: AnularRemitoVentaDto): Promise<void> => {
    await api.post<void>(`/api/RemitosVenta/${id}/anular`, payload);
  },

  getNotaEnvioPDF: async (id: number, bultos: number): Promise<{ blob: Blob; fileName: string | null }> => {
    const response = await api.get(`/api/RemitosVenta/${id}/nota-envio`, {
      params: { copias: bultos },
      responseType: 'blob',
    });
    const cd = (response.headers as Record<string, string>)?.['content-disposition'];
    return {
      blob: new Blob([response.data], { type: 'application/pdf' }),
      fileName: getFileNameFromContentDisposition(cd),
    };
  },

  updateRemitoVenta: async (id: number, payload: RemitoVentaCreateDto): Promise<void> => {
    await api.put(`/api/RemitosVenta/${id}`, payload);
  },

  createRemitoVenta: async (payload: RemitoVentaCreateDto): Promise<RemitoVenta> => {
    // Enviar cantidad/UM/precio tal como los ingresó el usuario; la API convierte a base y guarda snapshot en *Umped.
    const itemsDocumento = payload.items;
    const requiereActualizacionPorPrecioCero = pedidoTieneLineasConPrecioCero(
      itemsDocumento.map(itemComoDetallePrecio),
    );
    const itemsParaPost = requiereActualizacionPorPrecioCero
      ? prepararItemsRemitoParaPost(itemsDocumento)
      : itemsDocumento;

    const payloadFinal: RemitoVentaCreateDto = {
      ...payload,
      items: itemsParaPost,
    };

    const response = await api.post<RemitoVenta>('/api/RemitosVenta', payloadFinal);

    if (requiereActualizacionPorPrecioCero) {
      const nroFormulario = extraerNroFormularioRemito(response.data);
      if (!nroFormulario) {
        throw new Error('El remito se creó pero no se pudo obtener su número para guardar líneas con precio cero.');
      }
      await remitoVentaService.updateRemitoVenta(nroFormulario, {
        ...payload,
        items: itemsDocumento,
      });
      return remitoVentaService.getRemitoVenta(nroFormulario);
    }

    return response.data;
  },
};

export default remitoVentaService;
